<script lang="ts" setup>
    import { inject } from 'Vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { type SurveyInList, type statusName as Tab } from './SurveysTypes';
    import { vTooltip } from '@shared/directives';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import { type TagsProvide } from '@shared/components/Tags/Tags.access';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const emit = defineEmits(['showPreview', 'endSurvey', 'clickedSurveyId']);

    const { survey, currentTab, tags, isCreateNew } = defineProps<{
        survey: SurveyInList;
        currentTab?: Tab;
        tags: TagsProvide;
        isCreateNew?: boolean;
    }>();

    const svenskaMaklarhusetAccountId = 'f22bd1e0-f384-4fe6-b028-397f5443de2f';

    const replaceWithEventName = (survey: SurveyInList) => {
        return survey.Title.replace('{{Event.Name}}', survey.Event.Name);
    };

    const showPreview = (survey: SurveyInList) => {
        emit('showPreview', survey.SurveyId);
    };

    const draftStatus = 10;
</script>

<script lang="ts"></script>

<template>
    <div class="flex-1" style="min-width: min(400px, 100%)">
        <h2 class="h4 item-heading">
            <a
                :role="isCreateNew ? 'button' : 'link'"
                :href="
                    isCreateNew
                        ? 'javascript:void(0)'
                        : '/Surveys/' + survey.SurveyId + (currentTab !== 'drafts' && currentTab !== 'templates' ? '/Statistics' : '')
                "
                @click="$emit('clickedSurveyId', survey.SurveyId)">
                <template v-if="survey.Event && survey.Event.Name">
                    <template v-if="survey.Title">{{ replaceWithEventName(survey) }}</template>
                    <template v-else>{{ $Resources.NoTitlePlaceholder }}</template>
                </template>
                <template v-else>
                    {{ survey.Title ? survey.Title : $Resources.NoTitlePlaceholder }}
                </template>
            </a>

            <a
                v-if="survey.Event"
                class="mini-label mini-label-info m-xxs-l"
                rel="noopener"
                target="_blank"
                :href="'/Events/' + survey.Event.EventId"
                v-tooltip.top="survey.Event.Name">
                {{ $Resources.AssociatedEventLabel }}
            </a>

            <button v-if="!isCreateNew" type="button" class="btn btn-subtle btn-xs" v-tooltip.top="$Resources.IssuePreviewButton" @click="showPreview(survey)">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
            </button>
        </h2>

        <p v-if="survey.SurveyName">
            {{ survey.SurveyName }}
        </p>

        <p v-if="survey.Event">
            <strong>{{ $Resources.IssueEventHeading }}:</strong>
            {{ survey.Event.Name }}
            <span v-if="survey.Event.Start">
                {{ '(' + formatDateTime(survey.Event.Start) + ')' }}
            </span>
        </p>

        <TagsList
            v-if="tags && survey.Tags.length > 0"
            :tags="survey.Tags"
            :selectedIds="tags.state.selectedIds"
            @toggleTag="(tag) => tags!.toggle(tag)"></TagsList>

        <p class="meta">
            <i class="fa fa-pencil" aria-hidden="true"></i>
            {{ $Resources.LastModifiedLabel }}
            <time :datetime="survey.LastModified">{{ formatDateTime(survey.LastModified) }}</time>
            {{ $Resources.GenericByLabel }}
            {{ survey.LastModifiedBy.Name }}
        </p>
    </div>
    <div
        class="align-self-center"
        style="min-width: min(200px, 100%)"
        v-if="(currentTab === 'scheduled' || currentTab === 'active') && contextAccess.state.currentAccount!.AccountId !== svenskaMaklarhusetAccountId">
        <p class="d-flex flex-wrap gap-sm align-items-baseline m-xs-b small">
            {{ currentTab === 'scheduled' ? $Resources.SurveyStartLabel + ' ' + formatDateTime(survey.Start) : $Resources.SurveyActiveLabel }}
            <button type="button" class="btn btn-default btn-sm" @click="$emit('endSurvey', survey.SurveyId)">
                {{ $Resources.SurveysEndButton }}
            </button>
        </p>
    </div>
    <div v-if="currentTab === 'active' || currentTab === 'ended'" style="min-width: min(240px, 100%)">
        <div class="d-flex col-gap-lg flex-wrap ug-values m-0-all">
            <div class="ug-value">
                <span class="ug-value-label">{{ $Resources.SurveyResponseCountLabel }}</span>
                <span class="ug-value-indicator m-xs-r" :class="survey.RespondentCount && 'success'"></span>
                {{ formatNumericText(survey.ResponseCount, { precision: 1, largeNumbers: true })
                }}{{
                    survey.ResponseLimit > 0 && survey.EventId === null
                        ? '/' + formatNumericText(survey.ResponseLimit, { precision: 1, largeNumbers: true })
                        : ''
                }}
            </div>
            <div class="ug-value">
                <span class="ug-value-label">{{ $Resources.SurveyRespondentCountLabel }}</span>
                <span class="ug-value-indicator m-xs-r" :class="survey.RespondentCount && 'success'"></span>
                {{ formatNumericText(survey.RespondentCount, { precision: 1, largeNumbers: true }) }}
            </div>
        </div>
    </div>
</template>

<style></style>
